import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardSubtitle,
  CardTitle,
  Field,
  Input,
  PasswordInput,
  Wordmark,
} from '@keobook/ui';
import { useAuth } from '../lib/auth';

export function SignIn() {
  const navigate = useNavigate();
  const { signIn, resolveMfa, mfaHint } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await signIn(email.trim(), password);
      if (result === 'mfa-required') {
        setNeedsMfa(true);
        return;
      }
      navigate('/', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyMfa(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await resolveMfa(mfaCode);
      navigate('/', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" tagline="Smart accounting, powered by AI" />
        </div>

        <Card size="xl">
          {needsMfa ? (
            <>
              <CardTitle>Two-factor verification</CardTitle>
              <CardSubtitle>
                Enter the 6-digit code from {mfaHint ?? 'your authenticator app'}.
              </CardSubtitle>
              <form onSubmit={onVerifyMfa} noValidate>
                <Field label="Authentication code" required>
                  <Input
                    value={mfaCode}
                    onChange={(e) =>
                      setMfaCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    placeholder="123456"
                  />
                </Field>
                {error && (
                  <div role="alert" className="kb-form-error">
                    {error}
                  </div>
                )}
                <Button block type="submit" disabled={submitting || mfaCode.length < 6}>
                  {submitting ? 'Verifying…' : 'Verify and sign in'}
                </Button>
              </form>
              <p className="kb-auth-switch">
                <button
                  type="button"
                  className="kb-linkbtn"
                  onClick={() => {
                    setNeedsMfa(false);
                    setMfaCode('');
                    setError(null);
                  }}
                >
                  ← Use a different account
                </button>
              </p>
            </>
          ) : (
            <>
              <CardTitle>Welcome back</CardTitle>
              <CardSubtitle>Sign in to your KEOBOOKS account.</CardSubtitle>

              <form onSubmit={onSubmit} noValidate>
                <Field label="Email address" required>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                  />
                </Field>

                <Field
                  label={
                    <span className="kb-field-label-row">
                      <span>Password</span>
                      <Link to="/forgot-password" className="kb-link-sm">
                        Forgot password?
                      </Link>
                    </span>
                  }
                  required
                >
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                  />
                </Field>

                {error && (
                  <div role="alert" className="kb-form-error">
                    {error}
                  </div>
                )}

                <Button block type="submit" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Sign in to KEOBOOKS'}
                </Button>
              </form>

              <p className="kb-auth-switch">
                {/* New trials go through Stripe Checkout on the marketing site
                    (card required), not a card-less in-app signup. */}
                Don't have an account?{' '}
                <a href="https://keobooks.com/pricing">Start your free trial →</a>
              </p>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
